import {
    AfterViewInit,
    Component,
    ElementRef,
    inject,
    Input,
    Renderer2,
    ViewChild
} from '@angular/core';

import {
    mobileCheck
} from "src/utilities";

import {
    ToolbarEventsService
} from "src/app/core/services/toolbar-events/toolbar-events.service";

import {
    debounceTime,
    distinctUntilChanged,
    fromEvent,
    Subject,
    takeUntil
} from "rxjs";

import {
    ConfigurationManagerService,
    EnvironmentInfoService
} from 'src/app/core';

import {
    autobind
} from 'src/app/models/decorators.model';

import {
    BarcodeManagerService
} from 'src/app/core';

@Component({
	standalone: false,
    selector: 'app-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
    host: { '[class]': 'hostClasses' }
})
export class SearchBarComponent implements AfterViewInit {
    public readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly barcodeManagerService = inject(BarcodeManagerService);
    private readonly renderer = inject(Renderer2);
    protected readonly configurationManagerService = inject(ConfigurationManagerService);

    private readonly onDestroy$ = new Subject<void>();
    icon = 'tune';
    iconFilled = '';
    isMobile = mobileCheck();
    openSearch = false;
    @Input() idSelector: string = 'searchBarcodeInput';
    @Input() isFixed = false;
    searchBarcodeInput: string = "";
    @ViewChild('quickFilter') quickFilter!: ElementRef<HTMLInputElement>;

    ngAfterViewInit(): void {
        fromEvent(this.quickFilter.nativeElement, 'input').pipe(distinctUntilChanged(), debounceTime(300)).subscribe((event) => this.inputChangedHandler((event.target as HTMLInputElement)?.value));
        this.toolbarEventsService.searchBarIcon$.pipe(takeUntil(this.onDestroy$), debounceTime(50)).subscribe((icon) => this.icon = icon);
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    get hostClasses() {
        return this.isFixed ? this.isMobile? 'tw-w-3/4 sm:tw-flex-[0.65_1_0%]': 'tw-w-1/2 sm:tw-flex-[0.65_1_0%]' : '';
    }

    inputChangedHandler(quickFilterValue: string) {
        if(!quickFilterValue) {
            this.toolbarEventsService.events.next({type: "toolbar.clear_search_bar"});
        } else if(quickFilterValue.length < 3 && this.configurationManagerService.getPreference('cashregister.enable_research_product_by_3_chars')) {
            return;
        }
        
        this.icon = quickFilterValue ? this.toolbarEventsService.searchBarIconFilled$.getValue() : this.toolbarEventsService.searchBarIcon$.getValue();
        this.toolbarEventsService.searchBarValue$.next(quickFilterValue);

        if(!this.toolbarEventsService.searchBarcode$.value) {
            this.toolbarEventsService.events.next({ type: 'toolbar.search', search: quickFilterValue });
        }
    }

    async clickAction() {
        switch(this.icon) {
            case 'barcode':
                if (this.isAppleMobile()) {
                    return this.focusBarcodeInput();
                }
            break;
            case 'photo_camera':
                if (this.isCameraBarcodeSupported()) {
                    return this.openBarcodeAndSearch();
                }
            break;
            default:
                const action = this.quickFilter.nativeElement.value ? this.toolbarEventsService.searchBarActionFilled$.getValue() : this.toolbarEventsService.searchBarAction$.getValue();
                this.toolbarEventsService.events.next(action || { type: 'grid.open_tool_panel', panel: 'filters' });
            break;
        }
    }

    clearSearch(event: MouseEvent) {
        // Svuoto il valore nel servizio
        this.toolbarEventsService.searchBarValue$.next('');
        
        // Svuoto fisicamente l'input nel DOM
        if (this.quickFilter) {
            this.quickFilter.nativeElement.value = '';
        }

        // Richiamo l'handler per resettare le icone e notificare la pulizia
        this.inputChangedHandler('');
    }

    // ANDROID
    @autobind
    private async openBarcodeAndSearch() {
        const barcode = await this.barcodeManagerService.openCameraBarcode();

        if (barcode) {
            this.toolbarEventsService.events.next({ type: 'toolbar.search_barcode', barcode: barcode })
        }
    }

    protected isCameraBarcodeSupported() {
        return this.environmentInfoService.isCameraBarcodeSupported();
    }

    // APPLE WORKAROUND
    protected isAppleMobile() {
        return this.environmentInfoService.isAppleMobile();
    }

    protected barcodeSubmitted() {
        if (!this.searchBarcodeInput.length) {
            return;
        }

        this.toolbarEventsService.searchBarValue$.next(this.searchBarcodeInput);
        this.toolbarEventsService.events.next({ type: 'toolbar.search_barcode', barcode: this.searchBarcodeInput })
        this.searchBarcodeInput = "";
    };

    private focusBarcodeInput() {
        const barcodeInput = this.renderer.selectRootElement(`#${this.idSelector}`);
        barcodeInput.focus();
        barcodeInput.value = '';
    };
}
